/**
 * "Fill this board from a file" — the hand-off (F-144): a create dialog (Kola's
 * own, or the desktop app's Send-to-Kola) picks a storyboard or script, the
 * board is created, and the agent imports the file the moment the board
 * opens (`pending-import-run.ts`) as an ordinary agent turn — so the
 * transcript, the Applied card, Ctrl+Z and Revert are exactly what a person
 * would get by attaching the file in the panel themselves.
 *
 * The file crosses the navigation in memory, mirrored to sessionStorage so a
 * reload between "Create" and the board's first sync does not lose it — and
 * so the DESKTOP app, which mounts the Kola client in the same window, can
 * hand a file over through the same key. Taken (removed) on first use: an
 * import runs once per hand-off.
 *
 * PURE: storage only. Nothing here reaches the API or the session, so the
 * desktop dialog can import it without dragging the Kola client's network
 * layer into its own bundle graph.
 */
import { isBoardKind, type AgentAttachment, type BoardKind } from "@kola/shared";

export interface PendingImport {
  boardId: string;
  attachment: AgentAttachment;
  /**
   * Apply the plan WITHOUT the review click. True for a board created with
   * the file (seconds old and empty — nothing a wrong plan could overwrite);
   * false when the file was sent to an EXISTING board, where the plan is
   * left proposed for the person to review like any other.
   */
  autoApply: boolean;
  /**
   * F-154: what the board is for. A Short's import is the whole job — the
   * file lands in its three columns as it is and no step tasks follow.
   * Absent = long-form (every hand-off made before the field).
   */
  kind?: BoardKind;
  /**
   * F-155: where the file sits in the host's folders (a Short filled from
   * Local Files). The import puts it on row 1 and a Place video button on
   * row 2, which moves the Short's video beside it.
   */
  sourcePath?: string;
}

const memory = new Map<string, PendingImport>();
const storageKey = (boardId: string) => `kola.agentImport.${boardId}`;

export function stashPendingImport(
  boardId: string,
  attachment: AgentAttachment,
  options: { autoApply?: boolean; kind?: BoardKind | undefined; sourcePath?: string | undefined } = {},
): void {
  const pending: PendingImport = { boardId, attachment, autoApply: options.autoApply ?? true };
  if (options.kind !== undefined) pending.kind = options.kind;
  if (options.sourcePath !== undefined && options.sourcePath !== "") pending.sourcePath = options.sourcePath;
  memory.set(boardId, pending);
  try {
    sessionStorage.setItem(storageKey(boardId), JSON.stringify(pending));
  } catch {
    // Storage full or unavailable — the in-memory copy still carries it.
  }
}

/** Is an import waiting for this board? (Not taken — the board opens in Grid for it, F-150.) */
export function hasPendingImport(boardId: string): boolean {
  if (memory.has(boardId)) return true;
  try {
    return sessionStorage.getItem(storageKey(boardId)) !== null;
  } catch {
    return false;
  }
}

/** The import waiting for this board, if any — removed as it is taken. */
export function takePendingImport(boardId: string): PendingImport | undefined {
  let pending = memory.get(boardId);
  memory.delete(boardId);
  try {
    if (pending === undefined) {
      const raw = sessionStorage.getItem(storageKey(boardId));
      if (raw !== null) pending = parsePending(JSON.parse(raw), boardId);
    }
    sessionStorage.removeItem(storageKey(boardId));
  } catch {
    // Unreadable storage is the same as nothing stored.
  }
  return pending;
}

function parsePending(value: unknown, boardId: string): PendingImport | undefined {
  if (typeof value !== "object" || value === null) return undefined;
  const record = value as {
    boardId?: unknown;
    attachment?: unknown;
    autoApply?: unknown;
    kind?: unknown;
    sourcePath?: unknown;
  };
  if (record.boardId !== boardId) return undefined;
  const a = record.attachment as { id?: unknown; name?: unknown; text?: unknown } | undefined;
  if (typeof a?.id !== "string" || typeof a.name !== "string" || typeof a.text !== "string") return undefined;
  const pending: PendingImport = {
    boardId,
    attachment: { id: a.id, name: a.name, text: a.text },
    autoApply: record.autoApply !== false,
  };
  if (isBoardKind(record.kind)) pending.kind = record.kind;
  if (typeof record.sourcePath === "string" && record.sourcePath !== "") pending.sourcePath = record.sourcePath;
  return pending;
}
